import { z } from 'zod';
import { ATTENDANCE_STATUS_VALUES, AUTO_LOGOUT_DELAY_BOUNDS, DEBUG_DURATION_MINUTES_OPTIONS, FLAG_AFTER_BOUNDS, SCHOOL_HOUR_BOUNDS, } from './constants.js';
import { normalizeClassName } from './utils.js';
import { SOZIALPASS_BOUNDS } from './sozialpass.js';
import { PROJECT_DAY_SERIES_MAX_DATES } from './project-day-series.js';
// Code-39 alphabet per CLAUDE.md §18.5 — keep escapes verbatim to match the spec.
// eslint-disable-next-line no-useless-escape
export const BARCODE_REGEX = /^[A-Z0-9\-.\$\/\+\%\* ]{1,50}$/;
export const BarcodeSchema = z
    .string()
    .min(1, 'Barcode darf nicht leer sein')
    .max(50, 'Barcode darf höchstens 50 Zeichen lang sein')
    .regex(BARCODE_REGEX, 'Ungültiger Barcode (nur Code-39-Zeichen, Großbuchstaben)');
export const LoginSchema = z.object({
    email: z.string().min(1, 'E-Mail darf nicht leer sein').email('Ungültige E-Mail-Adresse'),
    password: z.string().min(1, 'Passwort darf nicht leer sein'),
});
// ---------------------------------------------------------------------------
// ProjectDay schemas (Prompt 11)
// ---------------------------------------------------------------------------
const DateStringSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum muss im Format YYYY-MM-DD vorliegen.');
const LabelSchema = z
    .string()
    .max(100, 'Bezeichnung darf höchstens 100 Zeichen lang sein.')
    .optional()
    .nullable();
const HourNumberSchema = z.coerce
    .number()
    .int()
    .min(SCHOOL_HOUR_BOUNDS.MIN, `Stunde muss ≥ ${SCHOOL_HOUR_BOUNDS.MIN} sein.`)
    .max(SCHOOL_HOUR_BOUNDS.MAX, `Stunde muss ≤ ${SCHOOL_HOUR_BOUNDS.MAX} sein.`);
// PA-Termin-Zeiten werden via SchoolHour-Raster derived (Prompt 20). Schema
// speichert nur die Stunden-Referenzen; getProjectDayTimes(pd, schoolHours)
// liefert die wall-clock-Strings für DTOs.
export const ProjectDayCreateSchema = z
    .object({
    date: DateStringSchema,
    hourStart: HourNumberSchema,
    hourEnd: HourNumberSchema,
    label: LabelSchema,
})
    .refine((v) => v.hourStart <= v.hourEnd, {
    message: 'hourStart muss <= hourEnd sein.',
    path: ['hourEnd'],
});
export const ProjectDayUpdateSchema = z
    .object({
    date: DateStringSchema.optional(),
    hourStart: HourNumberSchema.optional(),
    hourEnd: HourNumberSchema.optional(),
    label: LabelSchema,
})
    .refine((v) => {
    if (v.hourStart !== undefined && v.hourEnd !== undefined) {
        return v.hourStart <= v.hourEnd;
    }
    return true;
}, {
    message: 'hourStart muss <= hourEnd sein.',
    path: ['hourEnd'],
});
// Serien-Anlage: das Frontend generiert die Termine via generateSeriesDates,
// lässt Ferien/Feiertage abwählen und schickt die verbleibende Liste explizit.
// Deshalb nimmt das Schema fertige Datums-Strings statt Serien-Parameter — der
// Server muss den Rhythmus nicht nachrechnen und die abgewählten Termine
// tauchen gar nicht erst auf.
export const ProjectDayBulkCreateSchema = z
    .object({
    dates: z
        .array(DateStringSchema)
        .min(1, 'Mindestens ein Termin muss ausgewählt sein.')
        .max(PROJECT_DAY_SERIES_MAX_DATES, `Höchstens ${PROJECT_DAY_SERIES_MAX_DATES} Termine pro Serie.`)
        .refine((dates) => new Set(dates).size === dates.length, {
        message: 'Die Terminliste enthält Dubletten.',
    }),
    hourStart: HourNumberSchema,
    hourEnd: HourNumberSchema,
    label: LabelSchema,
})
    .refine((v) => v.hourStart <= v.hourEnd, {
    message: 'hourStart muss <= hourEnd sein.',
    path: ['hourEnd'],
});
// ---------------------------------------------------------------------------
// Heatmap query / response (Prompt 11)
// ---------------------------------------------------------------------------
export const HEATMAP_MAX_CLASSES = 5;
export const HeatmapQuerySchema = z.object({
    classNames: z
        .string()
        .min(1, 'classNames darf nicht leer sein.')
        .transform((v) => v
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0))
        .refine((arr) => arr.length >= 1, {
        message: 'Mindestens eine Klasse erforderlich.',
    }),
    from: DateStringSchema.optional(),
    to: DateStringSchema.optional(),
});
// ---------------------------------------------------------------------------
// Stats endpoints (Prompt 12)
// ---------------------------------------------------------------------------
export const StatsRangeSchema = z.object({
    from: DateStringSchema.optional(),
    to: DateStringSchema.optional(),
});
export const StatsTrendsQuerySchema = StatsRangeSchema.extend({
    groupBy: z.enum(['class', 'day']).default('day'),
});
export const StatsDawdleQuerySchema = StatsRangeSchema.extend({
    groupBy: z.enum(['class', 'day', 'student']).default('class'),
});
// ---------------------------------------------------------------------------
// Reports endpoints (room + student aggregations)
// ---------------------------------------------------------------------------
export const ReportsRangeSchema = StatsRangeSchema;
export const ReportsStudentQuerySchema = StatsRangeSchema.extend({
    className: z.string().min(1).optional(),
});
export const DAWDLE_HISTOGRAM_BUCKETS = [
    '0-2',
    '3-5',
    '6-10',
    '11-15',
    '16-30',
    '30+',
];
/**
 * Bucket boundaries (inclusive lower, inclusive upper). The last bucket has
 * no upper bound (Infinity).
 */
export const DAWDLE_HISTOGRAM_BUCKET_DEFS = [
    { bucket: '0-2', min: 0, max: 2 },
    { bucket: '3-5', min: 3, max: 5 },
    { bucket: '6-10', min: 6, max: 10 },
    { bucket: '11-15', min: 11, max: 15 },
    { bucket: '16-30', min: 16, max: 30 },
    { bucket: '30+', min: 31, max: Number.POSITIVE_INFINITY },
];
export const EARLY_WARNING_INDICATORS = [
    'absences_3_in_4_weeks',
    'declining_trend',
    'consecutive_late_2',
    'high_dawdle_avg',
];
// ---------------------------------------------------------------------------
// Admin: Settings (Prompt 13)
// ---------------------------------------------------------------------------
const MinuteBound = z.coerce
    .number()
    .int()
    .min(0, 'Wert darf nicht negativ sein.')
    .max(60, 'Wert darf höchstens 60 Minuten betragen.');
const AutoLogoutDelayBound = z.coerce
    .number()
    .int()
    .min(AUTO_LOGOUT_DELAY_BOUNDS.MIN, 'Wert darf nicht negativ sein.')
    .max(AUTO_LOGOUT_DELAY_BOUNDS.MAX, `Wert darf höchstens ${AUTO_LOGOUT_DELAY_BOUNDS.MAX} Minuten betragen.`);
const FlagAfterBound = z.coerce
    .number()
    .int()
    .min(FLAG_AFTER_BOUNDS.MIN, 'Wert darf nicht negativ sein.')
    .max(FLAG_AFTER_BOUNDS.MAX, `Wert darf höchstens ${FLAG_AFTER_BOUNDS.MAX} Minuten betragen.`);
// OU-Erweiterung (Prompt OU-1a): Bounds für die neuen Settings-Felder.
const DayWindowBound = z.coerce
    .number()
    .int()
    .min(0, 'Wert darf nicht negativ sein.')
    .max(30, 'Wert darf höchstens 30 Tage betragen.');
const RatingScaleMaxBound = z.coerce
    .number()
    .int()
    .min(2, 'Skala muss mindestens 2 Stufen haben.')
    .max(10, 'Skala darf höchstens 10 Stufen haben.');
// Sozialpass (OU-2a): Stunden sind fixe Einheiten (1 Eintrag == 1 Stunde),
// darum reine Ganzzahl-Bounds ohne Nachkommastellen.
const SozialpassCountBound = z.coerce
    .number()
    .int()
    .min(SOZIALPASS_BOUNDS.REQUIRED_PER_YEAR.MIN, 'Wert darf nicht negativ sein.')
    .max(SOZIALPASS_BOUNDS.REQUIRED_PER_YEAR.MAX, `Wert darf höchstens ${SOZIALPASS_BOUNDS.REQUIRED_PER_YEAR.MAX} Stunden betragen.`);
const SozialpassWarnDaysBound = z.coerce
    .number()
    .int()
    .min(SOZIALPASS_BOUNDS.WARN_DAYS_BEFORE.MIN, 'Wert darf nicht negativ sein.')
    .max(SOZIALPASS_BOUNDS.WARN_DAYS_BEFORE.MAX, `Wert darf höchstens ${SOZIALPASS_BOUNDS.WARN_DAYS_BEFORE.MAX} Tage betragen.`);
export const AdminSettingsUpdateSchema = z
    .object({
    schoolName: z
        .string()
        .min(1, 'Schulname darf nicht leer sein.')
        .max(200, 'Schulname darf höchstens 200 Zeichen lang sein.'),
    lateGraceMinutes: MinuteBound,
    dawdleWarnMinutes: MinuteBound,
    dawdleAlertMinutes: MinuteBound,
    praxismodulReturnGraceMinutes: MinuteBound,
    autoLogoutDelayMinutes: AutoLogoutDelayBound,
    flagAfterMinutes: FlagAfterBound,
    autoCheckoutEnabled: z.boolean(),
    iservSsoEnabled: z.boolean(),
    iservAutoCreateTeachers: z.boolean(),
    webuntisEnabled: z.boolean(),
    // OU-Erweiterung (Prompt OU-1a) — alle optional für Backward-Compat:
    // das Frontend kennt diese Felder noch nicht und sendet sie nicht mit.
    assessmentEnabled: z.boolean().optional(),
    assessmentRatingEnabled: z.boolean().optional(),
    assessmentRatingRequired: z.boolean().optional(),
    assessmentScaleMax: RatingScaleMaxBound.optional(),
    assessmentEditWindowDays: DayWindowBound.optional(),
    selfReportEnabled: z.boolean().optional(),
    selfReportReminderMinutes: MinuteBound.optional(),
    selfReportLateWindowDays: DayWindowBound.optional(),
    selfReportEditWindowMinutes: MinuteBound.optional(),
    studentLoginEnabled: z.boolean().optional(),
    // Sozialpass (OU-2a) — ebenfalls optional: aeltere Clients senden sie nicht.
    sozialpassEnabled: z.boolean().optional(),
    sozialpassRequiredPerYear: SozialpassCountBound.optional(),
    sozialpassRequiredFirstHalf: SozialpassCountBound.optional(),
    sozialpassWarnDaysBefore: SozialpassWarnDaysBound.optional(),
})
    .refine((v) => v.dawdleAlertMinutes >= v.dawdleWarnMinutes, {
    message: 'Alarm-Schwelle muss ≥ Warn-Schwelle sein.',
    path: ['dawdleAlertMinutes'],
    params: { code: 'INVALID_DAWDLE_THRESHOLDS' },
})
    .refine((v) => v.flagAfterMinutes < v.autoLogoutDelayMinutes, {
    message: 'Flag-Threshold muss kleiner als Auto-Logout-Delay sein.',
    path: ['flagAfterMinutes'],
    params: { code: 'INVALID_FLAG_THRESHOLDS' },
})
    // Sozialpass: das Halbjahres-Teilziel kann nie ueber dem Jahresziel liegen.
    .refine((v) => v.sozialpassRequiredFirstHalf === undefined ||
    v.sozialpassRequiredPerYear === undefined ||
    v.sozialpassRequiredFirstHalf <= v.sozialpassRequiredPerYear, {
    message: 'Soll im 1. Halbjahr darf das Jahres-Soll nicht überschreiten.',
    path: ['sozialpassRequiredFirstHalf'],
    params: { code: 'INVALID_SOZIALPASS_TARGETS' },
});
// ---------------------------------------------------------------------------
// Admin: Teachers (Prompt 13)
// ---------------------------------------------------------------------------
export const TeacherCreateSchema = z.object({
    email: z.string().email('Ungültige E-Mail-Adresse.'),
    fullName: z
        .string()
        .min(1, 'Name darf nicht leer sein.')
        .max(200, 'Name darf höchstens 200 Zeichen lang sein.'),
    isAdmin: z.boolean().optional().default(false),
    password: z.string().min(8, 'Passwort muss mind. 8 Zeichen haben.').optional(),
});
// ---------------------------------------------------------------------------
// Admin: manual single-student create
// ---------------------------------------------------------------------------
/**
 * Validates the body of `POST /api/v1/admin/students` (manual single-student
 * create). Shared by the backend route and the CreateStudentModal form.
 *
 * Normalization mirrors the CSV bulk-import parser: `className` is lowercased/
 * trimmed via `normalizeClassName`, and `schildId` is uppercased so it doubles
 * as the Code-39 `barcode` (CLAUDE.md §18.5). The regex check runs on the
 * already-uppercased value so lowercase input is accepted and normalized rather
 * than rejected.
 */
/**
 * Minimal student-email validation. IServ login schemas vary between schools, so
 * we only require an `@` with a dotted domain part — not RFC-complete by design.
 * Shared by the manual-create Zod schema and the CSV bulk-import row validator.
 */
export const STUDENT_EMAIL_REGEX = /^\S+@\S+\.\S+$/;
export const StudentCreateSchema = z.object({
    firstName: z
        .string()
        .trim()
        .min(1, 'Vorname darf nicht leer sein.')
        .max(100, 'Vorname darf höchstens 100 Zeichen lang sein.'),
    lastName: z
        .string()
        .trim()
        .min(1, 'Nachname darf nicht leer sein.')
        .max(100, 'Nachname darf höchstens 100 Zeichen lang sein.'),
    className: z
        .string()
        .trim()
        .min(1, 'Klasse darf nicht leer sein.')
        .max(20, 'Klasse darf höchstens 20 Zeichen lang sein.')
        .transform(normalizeClassName),
    schildId: z
        .string()
        .trim()
        .min(1, 'SchILD-ID darf nicht leer sein.')
        .max(50, 'SchILD-ID darf höchstens 50 Zeichen lang sein.')
        .transform((s) => s.toUpperCase())
        .refine((s) => BARCODE_REGEX.test(s), 'Ungültige SchILD-ID (nur Code-39-Zeichen).'),
    email: z
        .string()
        .trim()
        .min(1, 'Email darf nicht leer sein.')
        .max(254, 'Email darf höchstens 254 Zeichen lang sein.')
        .transform((s) => s.toLowerCase())
        .refine((s) => STUDENT_EMAIL_REGEX.test(s), 'Ungültige Email-Adresse.'),
});
export const TeacherRoleUpdateSchema = z.object({
    isAdmin: z.boolean(),
});
// ---------------------------------------------------------------------------
// Admin: Rooms / Kiosks (Prompt 13)
// ---------------------------------------------------------------------------
export const RoomCreateSchema = z.object({
    name: z
        .string()
        .min(1, 'Raumname darf nicht leer sein.')
        .max(100, 'Raumname darf höchstens 100 Zeichen lang sein.'),
    description: z.string().max(500).optional().nullable(),
});
const KioskNameSchema = z
    .string()
    .min(1, 'Kiosk-Name darf nicht leer sein.')
    .max(100, 'Kiosk-Name darf höchstens 100 Zeichen lang sein.');
export const KioskCreateSchema = z.object({
    name: KioskNameSchema,
    roomId: z.coerce.number().int().positive(),
});
// Umzug eines Kiosk-Geräts in einen anderen Raum bzw. Umbenennung. Beide
// Felder sind optional, aber mindestens eines muss gesetzt sein — ein leerer
// PUT ist ein Client-Fehler, kein No-op.
//
// Der Token bleibt unberührt: das iPad muss nicht neu eingerichtet werden,
// weil der Server den Raum bei jedem Request frisch aus dem Token auflöst.
export const KioskUpdateSchema = z
    .object({
    name: KioskNameSchema.optional(),
    roomId: z.coerce.number().int().positive().optional(),
})
    .refine((v) => v.name !== undefined || v.roomId !== undefined, {
    message: 'Es muss mindestens ein Feld geändert werden.',
});
// ---------------------------------------------------------------------------
// Admin: AuditLog filters (Prompt 13)
// ---------------------------------------------------------------------------
export const AuditLogQuerySchema = z.object({
    from: DateStringSchema.optional(),
    to: DateStringSchema.optional(),
    teacherId: z.coerce.number().int().positive().optional(),
    action: z.string().min(1).max(100).optional(),
    before: z.string().datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
// ---------------------------------------------------------------------------
// Admin: Assessment-Korrektur-Liste (ROADMAP #34)
// ---------------------------------------------------------------------------
export const AssessmentAdminListQuerySchema = z.object({
    // `lockedOnly` arrives as a query string — an explicit enum keeps "false"
    // from being coerced to `true` (which `z.coerce.boolean()` would do).
    lockedOnly: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
    before: z.string().datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
// ---------------------------------------------------------------------------
// Admin: Maintenance (Prompt 13)
// ---------------------------------------------------------------------------
/**
 * Confirmation token for the school-year reset. Must match exactly "RESET" —
 * trim/lowercase variants are rejected (CLAUDE.md §1, Prompt 13).
 */
export const SchoolYearResetSchema = z.object({
    confirmation: z.literal('RESET', {
        errorMap: () => ({ message: 'Bestätigung muss exakt "RESET" sein.' }),
    }),
});
export const RESTORE_TEST_OVERDUE_DAYS = 90;
// ---------------------------------------------------------------------------
// Dashboard CSV Export (Prompt 14)
// ---------------------------------------------------------------------------
export const EXPORT_FORMATS = ['csv'];
export const EXPORT_TYPES = ['webuntis', 'standard'];
const BoolFromString = z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => v === true || v === 'true');
// Accepts either repeated `status=` query params, a single comma-separated
// value (`status=fehlt,verspaetet`), or absent — the route surfaces a
// validated `AttendanceStatus[]` either way.
const StatusFilterSchema = z
    .union([
    z.array(z.enum(ATTENDANCE_STATUS_VALUES)),
    z
        .string()
        .min(1)
        .transform((s) => s.split(',').map((p) => p.trim())),
])
    .transform((value) => {
    const arr = Array.isArray(value) ? value : [value];
    const allowed = new Set(ATTENDANCE_STATUS_VALUES);
    return arr
        .map((s) => String(s))
        .filter((s) => allowed.has(s))
        .map((s) => s);
})
    .optional();
export const ExportQuerySchema = z.object({
    format: z.enum(EXPORT_FORMATS).default('csv'),
    type: z.enum(EXPORT_TYPES).default('webuntis'),
    from: DateStringSchema,
    to: DateStringSchema,
    className: z.string().min(1).optional(),
    includeDawdle: BoolFromString.optional().default(false),
    /** Optional status filter — empty/undefined means "all". */
    status: StatusFilterSchema,
    /** Hard cap on emitted rows after sorting. Omitted means no cap. */
    limit: z.coerce.number().int().positive().max(10_000).optional(),
});
export const WebuntisSmartExportBodySchema = z.object({
    from: DateStringSchema,
    to: DateStringSchema,
    classNames: z.array(z.string().min(1)).min(1, 'Mindestens eine Klasse erforderlich.'),
});
/**
 * Webuntis status mapping (CLAUDE.md §1 + §23 — handübertrag).
 * EXCUSED_PRAXISMODUL maps to "E" (entschuldigt) — Praxismodul-Schüler waren
 * regulär entschuldigt, kein Fehlen einzutragen.
 */
export const WEBUNTIS_STATUS_MAP = {
    anwesend: 'A',
    verspaetet: 'V',
    fehlt: 'F',
    excused_praxismodul: 'E',
};
export const CSV_BOM = '﻿';
// ---------------------------------------------------------------------------
// Debug-Modus / Test-PA (Prompt 25)
// ---------------------------------------------------------------------------
export const StartTestPaSchema = z.object({
    durationMinutes: z
        .number()
        .int()
        .refine((v) => DEBUG_DURATION_MINUTES_OPTIONS.includes(v), { message: 'Ungültige Dauer für Test-PA.' }),
});
//# sourceMappingURL=schemas.js.map